import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption } from '../modules/tcm-core.module';
import { UpdateRefundDepositsRequest, TerminalCancelRequest, CCSaleTerminalRequest, CreateTransactionFromClaimRequest, PatientPaymentTransactionLineRowViewModel, PaymentCreditCardViewModel, TerminalTransactionStatusCheckRequest, TransactionExpandEnum, TransactionRequest, UpdateDepositRequest, CreditCardViewModel, DepositSearchFilterViewModel, VoidRefundsResult } from '../models/payments';
import { PaymentMethodEnum } from '../models/enums';
import { FortisBaseResponse } from '../models/clinics/fortis/fortis-base-response';
import { TransactionResponse } from '../models/payments';
import { CCSaleKeyedRequest, CCSaleKeyedResponse } from '../models/payments';
import { FortisReceiptModel, ProcessFortisPaymentsResult } from '../models/payments';
import { CCRefundKeyedRequest, CCRefundTerminalRequest } from '../models/payments';
import { SalesOrderRefundViewModel } from '../models/sales-orders';
import { PaymentActionEnum } from '../models/enums/payment-action-enum';

@Injectable({
  providedIn: 'root'
})
export class PaymentDataService {
  constructor(private readonly httpClient: ApiHttpClient) {
  }

  public addClaimToPayment(claimId: number, depositId: number, cancellationToken: Promise<any>): Promise<number> {
    const request: CreateTransactionFromClaimRequest = { depositId: depositId, claimId: claimId };
    return this.httpClient.postJsonAsync('InsurancePayments/CreateTransactionFromClaim', request, cancellationToken);
  }

  public refundPayment(depositId: number, refundMethod: PaymentMethodEnum, cancellationToken: Promise<any>): Promise<boolean> {
    return this.httpClient.getJsonAsync('Payments/RefundPayment', { depositId: depositId, refundMethod: refundMethod }, cancellationToken);
  }

  public acceptinsurancePayment(transactionId: number, cancellationToken: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('InsurancePayments/AcceptInsurancePayment', transactionId, cancellationToken);
  }

  public getTransactionClaimsOptionsAsync(query: string, depositId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePayments/GetTransactionClaimsOptions', { searchString: query, depositId: depositId }, cancellationToken);
  }

  public acceptTransmittalPayment(claimBatchId: number, claimIds: number[], cancellationToken: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('InsurancePayments/AcceptTransmittalPayment', { claimBatchId, claimIds }, cancellationToken);
  }

  public voidPaymentAsync(depositId: number, cancellationToken: Promise<any>): Promise<boolean> {
    return this.httpClient.postJsonAsync('Payments/VoidPayment', depositId, cancellationToken);
  }

  public getTransactionLinesAsync(salesOrderId: number, cancellationToken: Promise<any>): Promise<PatientPaymentTransactionLineRowViewModel[]> {
    return this.httpClient.postJsonAsync('Payments/GetTransactionLines', { salesOrderId: salesOrderId }, cancellationToken);
  }

  public getPatientDocumentReceiptAsync(depositId: number): Promise<any> {
    return this.httpClient.getJsonAsync('Payments/GetPatientDocumentReceipt', { depositId: depositId });
  }

  public getPatientDocumentReportAsync(cancellationToken: Promise<any>): Promise<number> {
    return this.httpClient.getJsonAsync('Payments/GetPatientDocumentReport', cancellationToken);
  }

  public checkIfObonoPaymentMethod(paymentMethod: PaymentMethodEnum, timeout?: Promise<any>): Promise<boolean> {
    return this.httpClient.getJsonAsync('Payments/CheckIfObonoPaymentMethod', { paymentMethod: paymentMethod }, timeout);
  }

  public createObonoNullReceipt(clinicId, timeout?: Promise<any>): Promise<number> {
    return this.httpClient.getJsonAsync('Payments/CreateObonoNullReceipt', { clinicId: clinicId }, timeout);
  }

  public createObonoInitialReceipt(clinicId, timeout?: Promise<any>): Promise<number> {
    return this.httpClient.getJsonAsync('Payments/CreateObonoInitialReceipt', { clinicId: clinicId }, timeout);
  }

  public SendAliviTransaction(aliviTransactionRequest: any) : Promise<boolean> {
    return this.httpClient.postJsonAsync('Payments/SendAliviTransaction', aliviTransactionRequest);
  }

  // FORTIS

  public createFortisContact(clinicId: number, patientId: number, salesOrderId: number, timeout?: Promise<any>): Promise<FortisBaseResponse<any>> {
    return this.httpClient.postJsonAsync('Payments/CreateFortisContact', { clinicId: clinicId, patientId: patientId, salesOrderId: salesOrderId });
  }

  public createTerminalRequestAsync(contactId: string, clinicId: number, depositAmount:number, salesOrderId: number, depositId: number, cancellationToken: Promise<any>): Promise<FortisBaseResponse<any>> {
    const request: CCSaleTerminalRequest = { 
      contactId: contactId, 
      clinicId: clinicId, 
      transactionAmount: depositAmount, 
      salesOrderId: salesOrderId,
      depositId: depositId
    };
    return this.httpClient.postJsonAsync('Payments/CreateTerminalRequest', request, cancellationToken);
  }

  public createRefundTerminalRequestAsync(refundPaymentInfo: SalesOrderRefundViewModel, contactId: string, clinicId: number, depositAmount:number, 
    salesOrderId: number, paymentAction: PaymentActionEnum,cancellationToken?: Promise<any>): Promise<FortisBaseResponse<any>> {
    const request: CCRefundTerminalRequest = { 
      refundPaymentInfo: refundPaymentInfo,
      contactId: contactId, 
      clinicId: clinicId, 
      refundAmount: depositAmount, 
      salesOrderId: salesOrderId,
      paymentAction: paymentAction
    };
    return this.httpClient.postJsonAsync('Payments/CreateRefundTerminalRequest', request);
  }

  public updateRefundDepositsAsync(salesOrderId: number, isSuccess: boolean, cancellationToken: Promise<any> = null): Promise<boolean> {
    const request: UpdateRefundDepositsRequest = { salesOrderId: salesOrderId, isSuccess: isSuccess }
    return this.httpClient.postJsonAsync('Payments/UpdateRefundDeposits', request);
  }

  public terminalTransactionStatusCheckAsync(clinicId: number, asyncTransactionId:string, salesOrderId: number, cancellationToken: Promise<any> = null): Promise<FortisBaseResponse<any>> {
    const request: TerminalTransactionStatusCheckRequest = { clinicId: clinicId, asyncTransactionId: asyncTransactionId, salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('Payments/TerminalTransactionStatusCheck', request);
  }

  public terminalCancelationActionAsync(clinicId: number, salesOrderId: number, cancellationToken: Promise<any> = null): Promise<FortisBaseResponse<any>> {
    const request: TerminalCancelRequest = {clinicId: clinicId, salesOrderId: salesOrderId};
    return this.httpClient.postJsonAsync('Payments/TerminalCancelationAction', request);
  }

  public keyedTransactionAsync(contactId: string, clinicId: number, salesOrderId: number, depositId: number, depositAmount:number, cardInfo: PaymentCreditCardViewModel, transactionLines: PatientPaymentTransactionLineRowViewModel[], timeout?: Promise<any>): Promise<FortisBaseResponse<CCSaleKeyedResponse>> {
    const request: CCSaleKeyedRequest = {
      fortisContactId: contactId,
      clinicId: clinicId,
      salesOrderId: salesOrderId,
      depositId: depositId,
      amount: depositAmount,
      cardInfo: cardInfo,
      transactionLines: transactionLines,
      expand: [TransactionExpandEnum.IsVoidable, TransactionExpandEnum.IsRefundable]
    };
    return this.httpClient.postJsonAsync('Payments/KeyedTransaction', request);
  }

  public refundKeyedTransactionAsync(refundPaymentInfo: SalesOrderRefundViewModel, contactId: string, clinicId: number, 
    salesOrderId: number, amount: number, cardInfo: CreditCardViewModel, timeout?: Promise<any>): Promise<FortisBaseResponse<CCSaleKeyedResponse>> {
    const request: CCRefundKeyedRequest = {
      refundPaymentInfo: refundPaymentInfo,
      fortisContactId: contactId,
      clinicId: clinicId,
      salesOrderId: salesOrderId,
      refundAmount: amount,
      cardInfo: cardInfo,
      expand: [TransactionExpandEnum.IsVoidable, TransactionExpandEnum.IsRefundable]
    };
    return this.httpClient.postJsonAsync('Payments/RefundKeyedTransaction', request);
  }

  public voidFortisPaymentAsync(clinicId: number, depositId: number, cancellationToken: Promise<any>): Promise<ProcessFortisPaymentsResult> {
    return this.httpClient.postJsonAsync('Payments/VoidFortisPayment', { clinicId: clinicId, depositId: depositId }, cancellationToken);
  }

  public voidRefundsAsync(salesOrderId: number, cancellationToken: Promise<any> = null): Promise<FortisBaseResponse<VoidRefundsResult>> {
    return this.httpClient.postJsonAsync('Payments/VoidRefunds', salesOrderId);
  }

  public getTransactionAsync(request: TransactionRequest, cancellationToken: Promise<any> = null): Promise<FortisBaseResponse<TransactionResponse>> {
    return this.httpClient.getJsonAsync('Payments/GetTransaction', request);
  }

  public updateDepositAsync(request: UpdateDepositRequest, cancellationToken: Promise<any> = null): Promise<number> {
    return this.httpClient.postJsonAsync('Payments/UpdateDeposit', request);
  }
  public updateDepositonRecheckAsync(request: UpdateDepositRequest, cancellationToken: Promise<any> = null): Promise<number> {
    return this.httpClient.postJsonAsync('Payments/UpdateDepositonRecheck', request);
  }

  public generateFortisReceiptAsync(depositId: number, regenerate: boolean = false): Promise<FortisBaseResponse<FortisReceiptModel>> {
    return this.httpClient.getJsonAsync('Payments/GenerateFortisReceipt', { depositId: depositId, regenerate: regenerate });
  }

  public getDepositsExportFile(request : DepositSearchFilterViewModel) {
   return this.httpClient.getFileAsync('InsurancePayments/GetDepositsExportFile', request, null, false);
  }

}
